// Event-firing snippet for Godot 4.5 / GDScript. Drop in as a second
// autoload alongside PlayerBasePairing; call `PlayerBaseEvents.fire(...)`
// at the moments you configured as events on the playtest flow.

export function buildEventGodotSnippet(apiKey: string, functionsBaseUrl: string): string {
  return `# res://addons/playerbase/player_base_events.gd
# Companion autoload to PlayerBasePairing. Register it after
# PlayerBasePairing in Project Settings > Autoload so the pairing
# singleton is ready when this one boots. Godot 4.5 / GDScript.
#
# Call from anywhere in your game:
#
#   PlayerBaseEvents.fire("tutorialComplete")
#   PlayerBaseEvents.fire("bossDefeated", { "boss": "stage1", "tries": 4 })
#
# Behaviour:
#   * No-op until the player has paired, so it's safe to call from
#     gameplay code without guarding.
#   * Generates a fresh idempotencyKey per fire, so retries collapse
#     server-side instead of double-counting.
#   * 200 covers ok / duplicate / no_active_playtest - all expected
#     during normal operation. Anything else logs a push_warning so
#     config drift in dev builds is visible without crashing gameplay.
extends Node

const API_KEY := "${apiKey}"
const EVENTS_URL := "${functionsBaseUrl}/events"

# Fire one of the SDK event names you configured on the playtest flow.
# Safe to call on every install - no-ops until the player has paired.
# \`payload\` is free-form and stored verbatim alongside the event for
# debugging in the session timeline.
func fire(event_name: String, payload: Dictionary = {}) -> void:
\tif not PlayerBasePairing.is_paired():
\t\treturn
\tvar body := JSON.stringify({
\t\t"playerToken": PlayerBasePairing.player_token,
\t\t"eventName": event_name,
\t\t"idempotencyKey": _new_uuid(),
\t\t"payload": payload,
\t\t"occurredAt": Time.get_datetime_string_from_system(true) + "Z",
\t})
\tvar signature := _sign(body)
\tvar req := HTTPRequest.new()
\tadd_child(req)
\treq.request_completed.connect(_on_event_completed.bind(req, event_name))
\tvar err := req.request(
\t\tEVENTS_URL,
\t\tPackedStringArray([
\t\t\t"content-type: application/json",
\t\t\t"x-playerbase-signature: " + signature,
\t\t]),
\t\tHTTPClient.METHOD_POST,
\t\tbody
\t)
\tif err != OK:
\t\treq.queue_free()
\t\tpush_warning("PlayerBase: failed to dispatch %s (transport)" % event_name)

func _on_event_completed(
\t_result: int,
\tresponse_code: int,
\t_headers: PackedStringArray,
\t_body: PackedByteArray,
\treq: HTTPRequest,
\tevent_name: String
) -> void:
\treq.queue_free()
\t# 200 covers ok / duplicate / no_active_playtest - all expected during
\t# normal operation. Warn on anything else so studios notice signature
\t# or config drift in dev builds.
\tif response_code != 200:
\t\tpush_warning("PlayerBase: %s returned %d" % [event_name, response_code])

func _sign(body: String) -> String:
\tvar hmac := HMACContext.new()
\thmac.start(HashingContext.HASH_SHA256, API_KEY.to_utf8_buffer())
\thmac.update(body.to_utf8_buffer())
\treturn hmac.finish().hex_encode()

func _new_uuid() -> String:
\tvar b := PackedByteArray()
\tb.resize(16)
\tfor i in 16:
\t\tb[i] = randi() & 0xff
\tb[6] = (b[6] & 0x0f) | 0x40
\tb[8] = (b[8] & 0x3f) | 0x80
\tvar h := b.hex_encode()
\treturn "%s-%s-%s-%s-%s" % [
\t\th.substr(0, 8), h.substr(8, 4), h.substr(12, 4),
\t\th.substr(16, 4), h.substr(20, 12),
\t]
`;
}
